import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { NotificationList } from "@/components/notifications/NotificationList";
import { Notification } from "@/types/notification";
import { toast } from "sonner";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useTheme } from "@/context/ThemeContext";
import {
  ColoredOutlineButton,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, InfoIcon } from "lucide-react";
import { Download } from "lucide-react";
import { getNotificationTemplate, resolveNotificationStatus, resolveNotificationTitle } from "@/components/notifications/notificationTemplates";
import { useNotifications } from "@/hooks/useNotifications";
import { useContext } from "react";
import { AuthContext } from "@/context/AuthContext";
import { notificationLabels, supportLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";

import { resolveNotificationNavigationPath } from "@/lib/navigation/notificationNavigation";
import { parseExternalUrlToAppRoute } from "@/lib/native/parseAppUrl";
import { cn } from "@/lib/utils";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Notifications = () => {
  const [popupNotification, setPopupNotification] =
    useState<Notification | null>(null);
  const isMobile = useIsMobile();
  const { isNative: isNativeApp } = useNativePlatform();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const currentUserId = String(authContext?.user?.id || "");
  const { language } = useLanguage();
  const notificationLabels_i18n = notificationLabels(language);
  const supportLabels_i18n = supportLabels(language);

  // Use the notifications hook (with pagination: default 10 per page)
  const {
    notifications,
    totalNotifications,
    totalPages,
    page,
    pageSize,
    setPage,
    setPageSize,
    pageSizeOptions,
    isLoadingNotifications,
    refetchNotifications,
    markAsRead,
    deleteNotification,
    markAllAsRead,
    deleteAllNotifications,
    isMarkingAllAsRead,
    isDeletingAllNotifications,
  } = useNotifications(10);

  // Use file and media hooks for downloads
  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();

  // Refetch notifications when component mounts (user navigates to this page)
  useEffect(() => {
    if (currentUserId) {
      refetchNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, not when refetchNotifications changes

  const handleNotificationClick = (notification: Notification) => {
    setPopupNotification(notification);
  };

  const handleDeleteNotification = (notificationId: number) => {
    deleteNotification({ notificationId, userId: currentUserId });
    toast.success(supportLabels_i18n.notificationDeleted);
  };

  const handleMarkNotificationAsRead = (notificationId: number) => {
    markAsRead({ notificationId, userId: currentUserId });
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead(currentUserId);
    toast.success(supportLabels_i18n.allNotificationsMarked);
  };

  const handleDeleteAll = () => {
    deleteAllNotifications(currentUserId);
    toast.success(supportLabels_i18n.allNotificationsDeleted);
  };

  const handleActionClick = (
    route: string,
    data?: Record<string, unknown>,
  ) => {
    closePopup();
    const inAppPath = resolveNotificationNavigationPath(route, data);
    if (inAppPath) {
      if (isNativeApp) {
        window.location.hash = `#${inAppPath}`;
      }
      navigate(inAppPath);
      return;
    }

    if (route.startsWith("http://") || route.startsWith("https://")) {
      if (isNativeApp) {
        const parsed = parseExternalUrlToAppRoute(route);
        if (parsed) {
          window.location.hash = `#${parsed}`;
          navigate(parsed);
          return;
        }
      }
      window.location.href = route;
    } else if (route) {
      navigate(route);
    }
  };

  const closePopup = () => {
    setPopupNotification(null);
  };

  return (
    <div
      className={cn(
        "min-h-[calc(100vh-4rem)] animate-fade-in",
        isNativeApp && "native-notifications-page",
      )}
    >
      <div
        className={cn(
          "md:max-w-[80%] mx-auto z-10",
          isNativeApp && "max-w-none w-full",
        )}
      >
        {!isMobile && <BtnNavigateBack />}

        <div className={`flex ${isMobile ? "flex-col items-center" : "gap-6"}`}>
          <div
            className={cn(
              "w-full max-w-3xl mx-auto",
              isNativeApp && "max-w-none w-full",
            )}
          >
            {isLoadingNotifications ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-500">
                  {supportLabels_i18n.loadingNotifications}
                </p>
              </div>
            ) : (
              <NotificationList
                notifications={notifications}
                totalNotifications={totalNotifications}
                totalPages={totalPages}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                onNotificationClick={handleNotificationClick}
                onDeleteNotification={handleDeleteNotification}
                onMarkAsRead={handleMarkNotificationAsRead}
                onMarkAllAsRead={handleMarkAllAsRead}
                onDeleteAll={handleDeleteAll}
                isMarkingAllAsRead={isMarkingAllAsRead}
                isDeletingAll={isDeletingAllNotifications}
              />
            )}
          </div>
        </div>

        <Dialog
          open={!!popupNotification}
          onOpenChange={(o) => {
            if (!o) closePopup();
          }}
        >
          <DialogContent
            compact
            sheetOpen={!!popupNotification}
            className="sm:max-w-md"
            aria-describedby={undefined}
          >
            {popupNotification &&
              (() => {
                const template = getNotificationTemplate(
                  popupNotification.type,
                  language,
                );
                if (!template) return null;

                const title = resolveNotificationTitle(
                  template,
                  popupNotification.data || {},
                );
                const description = template.description(
                  popupNotification.data,
                );
                const status = resolveNotificationStatus(
                  template,
                  popupNotification.data || {},
                );
                const action = template.action;

                return (
                  <>
                    <DialogHeader className="text-left space-y-2">
                      <div>
                        <Badge
                          className={`text-xs ${
                            status === "action_required"
                              ? "bg-orange-500/10 text-orange-700"
                              : status === "completed"
                                ? "bg-green-500/10 text-green-500"
                                : "bg-blue-500/10 text-blue-500"
                          }`}
                        >
                          {status === "action_required" && (
                            <AlertCircle className="h-3 w-3 mr-1" />
                          )}
                          {status === "completed" && (
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                          )}
                          {status === "info" && (
                            <InfoIcon className="h-3 w-3 mr-1" />
                          )}
                          {status === "action_required"
                            ? notificationLabels_i18n.status?.actionRequired
                            : status === "completed"
                              ? notificationLabels_i18n.status?.completed
                              : notificationLabels_i18n.status?.info}
                        </Badge>
                      </div>
                      <DialogTitle className="text-lg font-bold pr-8">
                        {title}
                      </DialogTitle>
                    </DialogHeader>
                    <p className="mb-4 whitespace-pre-line text-muted-foreground">
                      {description}
                    </p>
                    <p className="text-sm mb-4 text-muted-foreground">
                      {popupNotification.timestamp}
                    </p>

                    {action && (
                      <div className="flex justify-end gap-2">
                        {action.isDownload ? (
                          <ColoredOutlineButton
                            type="button"
                            variant="blue"
                            tintedBackground
                            size="default"
                            onClick={async () => {
                              try {
                                const notificationData =
                                  popupNotification.data as Record<
                                    string,
                                    unknown
                                  >;

                                const fileName =
                                  (notificationData?.fileName as string) ||
                                  (popupNotification.type === "invoice_sent"
                                    ? "faktura.pdf"
                                    : "kontrakt.pdf");

                                let downloadUrl: string;

                                if (
                                  notificationData?.mediaId &&
                                  typeof notificationData.mediaId === "string"
                                ) {
                                  downloadUrl = await getDownloadUrlAsync({
                                    mediaId: notificationData.mediaId,
                                    expiresIn: 3600,
                                  });
                                } else {
                                  const apiRoute =
                                    typeof action.route === "function"
                                      ? action.route({
                                          ...notificationData,
                                          notificationId: popupNotification.id,
                                        })
                                      : action.route;

                                  const response = await fetch(apiRoute, {
                                    method: "GET",
                                    credentials: "include",
                                    redirect: "manual",
                                  });

                                  if (
                                    response.status === 302 ||
                                    response.status === 301
                                  ) {
                                    const location =
                                      response.headers.get("Location");
                                    if (!location) {
                                      throw new Error(
                                        "No redirect location found",
                                      );
                                    }
                                    downloadUrl = location;
                                  } else if (response.ok) {
                                    const data = (await response.json()) as {
                                      downloadUrl?: string;
                                      url?: string;
                                    };
                                    downloadUrl =
                                      data.downloadUrl || data.url || apiRoute;
                                  } else {
                                    throw new Error(
                                      `Failed to get download URL: ${response.statusText}`,
                                    );
                                  }
                                }

                                await downloadFile(downloadUrl, fileName);

                                toast.success(
                                  notificationLabels_i18n.defaults
                                    ?.downloadStarted ?? "Nedladdning startad",
                                );

                                handleMarkNotificationAsRead(
                                  popupNotification.id,
                                );
                              } catch (error) {
                                const errorMessage =
                                  error instanceof Error
                                    ? error.message
                                    : supportLabels_i18n.unknownError;
                                toast.error(
                                  supportLabels_i18n.downloadFailed.replace(
                                    "{error}",
                                    errorMessage,
                                  ),
                                );
                              }
                            }}
                          >
                            <Download
                              className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                              strokeWidth={2.35}
                            />
                            <span>
                              {typeof action.label === "function"
                                ? action.label(popupNotification.data || {})
                                : action.label}
                            </span>
                          </ColoredOutlineButton>
                        ) : (
                          <ColoredOutlineButton
                            type="button"
                            variant="blue"
                            tintedBackground
                            size="default"
                            onClick={() => {
                              const route =
                                typeof action.route === "function"
                                  ? action.route(popupNotification.data || {})
                                  : action.route;
                              handleActionClick(
                                route,
                                popupNotification.data as
                                  | Record<string, unknown>
                                  | undefined,
                              );
                            }}
                          >
                            {typeof action.label === "function"
                              ? action.label(popupNotification.data || {})
                              : action.label}
                          </ColoredOutlineButton>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Notifications;
